import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Trash2, Plus, ArrowLeft, ExternalLink, Eye, EyeOff,
  Type, Image as ImageIcon, Quote, GripVertical, ChevronUp, ChevronDown,
} from "lucide-react";
import { Field, TextInput, TextArea, SectionTitle, Subtitle } from "./Field";
import { ImageField } from "./ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);

const newId = () => Math.random().toString(36).slice(2, 10);

const emptyBlock = (type) => {
  const id = newId();
  if (type === "text") return { id, type: "text", text: "" };
  if (type === "image") return { id, type: "image", url: "", caption: "", alt: "" };
  if (type === "quote") return { id, type: "quote", text: "", attribution: "" };
  return null;
};

const emptyPage = () => ({
  id: null,
  slug: "",
  title: "",
  eyebrow: "",
  hero_image_url: "",
  blocks: [],
  is_published: false,
  show_in_nav: false,
  nav_label: "",
  order: 100,
});

// ----- Block editor -----
const BlockEditor = ({ block, onChange, onRemove, onMove, isFirst, isLast, token }) => {
  const upd = (patch) => onChange({ ...block, ...patch });

  const TypeIcon = block.type === "text" ? Type : block.type === "image" ? ImageIcon : Quote;
  const TYPE_LABELS = { text: "Tekst", image: "Imazh", quote: "Citat" };

  return (
    <div
      data-testid={`page-block-${block.id}`}
      className="border border-sm-border bg-sm-bg p-5"
    >
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-sm-border">
        <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive">
          <GripVertical className="w-3.5 h-3.5" />
          <TypeIcon className="w-3.5 h-3.5" />
          {TYPE_LABELS[block.type]}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={isFirst}
            data-testid={`page-block-${block.id}-up`}
            className="p-1.5 border border-sm-border hover:border-sm-olive disabled:opacity-30"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={isLast}
            data-testid={`page-block-${block.id}-down`}
            className="p-1.5 border border-sm-border hover:border-sm-olive disabled:opacity-30"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            data-testid={`page-block-${block.id}-remove`}
            className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50 ml-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {block.type === "text" && (
        <TextArea
          rows={5}
          value={block.text}
          onChange={(v) => upd({ text: v })}
          placeholder="Shkruani tekstin këtu..."
          testid={`page-block-${block.id}-text`}
        />
      )}
      {block.type === "image" && (
        <div className="space-y-4">
          <ImageField
            label="Imazhi"
            value={block.url}
            onChange={(v) => upd({ url: v })}
            token={token}
            testid={`page-block-${block.id}-image`}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Përshkrim (caption)">
              <TextInput
                value={block.caption}
                onChange={(v) => upd({ caption: v })}
                testid={`page-block-${block.id}-caption`}
              />
            </Field>
            <Field label="Alt text">
              <TextInput
                value={block.alt}
                onChange={(v) => upd({ alt: v })}
                testid={`page-block-${block.id}-alt`}
              />
            </Field>
          </div>
        </div>
      )}
      {block.type === "quote" && (
        <div className="space-y-3">
          <TextArea
            rows={3}
            value={block.text}
            onChange={(v) => upd({ text: v })}
            placeholder="Citat..."
            testid={`page-block-${block.id}-quote`}
          />
          <TextInput
            value={block.attribution}
            onChange={(v) => upd({ attribution: v })}
            placeholder="Atribuim (p.sh. Chef Ardit)"
            testid={`page-block-${block.id}-attribution`}
          />
        </div>
      )}
    </div>
  );
};

// ----- Page form -----
const PageForm = ({ page, token, onBack, onSaved }) => {
  const [data, setData] = useState(page || emptyPage());
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!!page?.id);

  useEffect(() => {
    setData(page || emptyPage());
    setSlugTouched(!!page?.id);
  }, [page]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const onTitle = (v) => {
    update({
      title: v,
      ...(slugTouched ? {} : { slug: slugify(v) }),
    });
  };

  const addBlock = (type) => {
    update({ blocks: [...(data.blocks || []), emptyBlock(type)] });
  };
  const updateBlock = (idx, next) => {
    update({ blocks: data.blocks.map((b, i) => (i === idx ? next : b)) });
  };
  const removeBlock = (idx) => {
    update({ blocks: data.blocks.filter((_, i) => i !== idx) });
  };
  const moveBlock = (idx, dir) => {
    const next = [...data.blocks];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    update({ blocks: next });
  };

  const save = async () => {
    if (!data.title.trim()) return toast.error("Titulli është i detyrueshëm.");
    if (!data.slug.trim()) return toast.error("Slug është i detyrueshëm.");

    setSaving(true);
    const payload = {
      slug: data.slug,
      title: data.title,
      eyebrow: data.eyebrow || "",
      hero_image_url: data.hero_image_url || "",
      blocks: data.blocks || [],
      is_published: !!data.is_published,
      show_in_nav: !!data.show_in_nav,
      nav_label: data.nav_label || "",
      order: Number(data.order) || 100,
    };
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let res;
      if (data.id) {
        res = await axios.put(`${API}/admin/pages/${data.id}`, payload, { headers });
      } else {
        res = await axios.post(`${API}/admin/pages`, payload, { headers });
      }
      toast.success("U ruajt.");
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gabim në ruajtje.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="page-form" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-sm-border">
        <button
          type="button"
          onClick={onBack}
          data-testid="page-form-back"
          className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Te lista
        </button>
        <div className="flex items-center gap-3">
          {data.id && data.is_published && (
            <a
              href={`/p/${data.slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid="page-form-preview"
              className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Preview
            </a>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="page-form-save"
            className="inline-flex items-center gap-2 bg-sm-text text-sm-bg px-5 py-2 text-[0.7rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </div>

      <div className="bg-sm-surface border border-sm-border p-6 md:p-10 space-y-8">
        <SectionTitle sub="Detajet themelore të faqes së re.">
          {data.id ? "Edito faqen" : "Faqe e re"}
        </SectionTitle>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Titulli *">
            <TextInput
              value={data.title}
              onChange={onTitle}
              placeholder="Historia Jonë"
              testid="page-form-title"
            />
          </Field>
          <Field label="Slug (URL) *">
            <div className="flex items-center border border-sm-border bg-sm-bg">
              <span className="px-3 text-[0.7rem] text-sm-muted tracking-wider border-r border-sm-border h-full flex items-center py-3">
                /p/
              </span>
              <input
                value={data.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  update({ slug: slugify(e.target.value) });
                }}
                placeholder="historia-jone"
                className="flex-1 bg-transparent px-3 py-3 text-sm outline-none"
                data-testid="page-form-slug"
              />
            </div>
          </Field>
          <Field label="Eyebrow (mbititull)">
            <TextInput
              value={data.eyebrow}
              onChange={(v) => update({ eyebrow: v })}
              placeholder="Rreth nesh"
              testid="page-form-eyebrow"
            />
          </Field>
          <Field label="Renditja në navigim">
            <TextInput
              value={String(data.order)}
              onChange={(v) => update({ order: v })}
              placeholder="100"
              testid="page-form-order"
            />
          </Field>
        </div>

        <ImageField
          label="Imazhi i hero-s (opsional)"
          value={data.hero_image_url}
          onChange={(v) => update({ hero_image_url: v })}
          token={token}
          testid="page-form-hero-image"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4 border-t border-sm-border">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!data.is_published}
              onChange={(e) => update({ is_published: e.target.checked })}
              data-testid="page-form-published"
              className="w-4 h-4 accent-sm-olive"
            />
            <span className="text-[0.7rem] tracking-[0.28em] uppercase font-medium">
              {data.is_published ? "Publikuar" : "Draft"}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!data.show_in_nav}
              onChange={(e) => update({ show_in_nav: e.target.checked })}
              data-testid="page-form-show-in-nav"
              className="w-4 h-4 accent-sm-olive"
            />
            <span className="text-[0.7rem] tracking-[0.28em] uppercase font-medium">
              Shfaq në menu
            </span>
          </label>
          <Field label="Etiketa në menu (opsionale)">
            <TextInput
              value={data.nav_label}
              onChange={(v) => update({ nav_label: v })}
              placeholder={data.title}
              testid="page-form-nav-label"
            />
          </Field>
        </div>

        <Subtitle>Përmbajtja — Blloqet</Subtitle>
        <div className="space-y-4">
          {(data.blocks || []).map((b, i) => (
            <BlockEditor
              key={b.id || i}
              block={b}
              onChange={(next) => updateBlock(i, next)}
              onRemove={() => removeBlock(i)}
              onMove={(dir) => moveBlock(i, dir)}
              isFirst={i === 0}
              isLast={i === data.blocks.length - 1}
              token={token}
            />
          ))}
          {(!data.blocks || data.blocks.length === 0) && (
            <div className="border border-dashed border-sm-border p-10 text-center">
              <p className="text-sm-muted text-sm font-light">
                Asnjë bllok ende. Shtoni blloqe nga butonat më poshtë.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => addBlock("text")}
            data-testid="page-form-add-text"
            className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> <Type className="w-3.5 h-3.5" /> Tekst
          </button>
          <button
            type="button"
            onClick={() => addBlock("image")}
            data-testid="page-form-add-image"
            className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> <ImageIcon className="w-3.5 h-3.5" /> Imazh
          </button>
          <button
            type="button"
            onClick={() => addBlock("quote")}
            data-testid="page-form-add-quote"
            className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> <Quote className="w-3.5 h-3.5" /> Citat
          </button>
        </div>
      </div>
    </div>
  );
};

// ----- List view -----
const PagesList = ({ pages, onNew, onEdit, onDelete }) => (
  <div data-testid="pages-list">
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-serif text-2xl text-sm-text">Faqet e krijuara</h2>
      <button
        type="button"
        onClick={onNew}
        data-testid="pages-new-btn"
        className="inline-flex items-center gap-2 bg-sm-text text-sm-bg px-5 py-2 text-[0.7rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Faqe e re
      </button>
    </div>

    {pages.length === 0 ? (
      <div className="border border-dashed border-sm-border p-16 text-center">
        <p className="text-sm-muted font-light">Nuk ka faqe ende. Krijoni një të re.</p>
      </div>
    ) : (
      <div className="border border-sm-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="pages-table">
          <thead className="bg-sm-surface text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive">
            <tr>
              <th className="text-left p-4">Titulli</th>
              <th className="text-left p-4">URL</th>
              <th className="text-left p-4">Statusi</th>
              <th className="text-left p-4">Në menu</th>
              <th className="text-left p-4">Blloqe</th>
              <th className="text-right p-4">Veprime</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr
                key={p.id}
                data-testid={`pages-row-${p.id}`}
                className="border-t border-sm-border hover:bg-sm-surface/50"
              >
                <td className="p-4">
                  <p className="font-medium text-sm-text">{p.title}</p>
                  {p.eyebrow && (
                    <p className="text-xs text-sm-muted mt-1">{p.eyebrow}</p>
                  )}
                </td>
                <td className="p-4 font-mono text-sm-muted">/p/{p.slug}</td>
                <td className="p-4">
                  {p.is_published ? (
                    <span className="inline-flex items-center gap-1 text-[0.65rem] tracking-[0.2em] uppercase px-2 py-1 border border-green-400 text-green-700 bg-green-50">
                      <Eye className="w-3 h-3" /> Publikuar
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[0.65rem] tracking-[0.2em] uppercase px-2 py-1 border border-sm-border text-sm-muted">
                      <EyeOff className="w-3 h-3" /> Draft
                    </span>
                  )}
                </td>
                <td className="p-4 text-sm-text">{p.show_in_nav ? "Po" : "—"}</td>
                <td className="p-4 text-sm-muted">{(p.blocks || []).length}</td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    {p.is_published && (
                      <a
                        href={`/p/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`pages-preview-${p.id}`}
                        className="p-2 border border-sm-border text-sm-muted hover:border-sm-olive hover:text-sm-olive transition-colors"
                        title="Preview"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => onEdit(p)}
                      data-testid={`pages-edit-${p.id}`}
                      className="px-3 py-2 border border-sm-border text-[0.65rem] tracking-[0.28em] uppercase hover:border-sm-olive hover:text-sm-olive transition-colors"
                    >
                      Edito
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(p)}
                      data-testid={`pages-delete-${p.id}`}
                      className="p-2 border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                      title="Fshi"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ----- Main -----
export const PagesEditor = ({ token, onUnauthorized }) => {
  const [view, setView] = useState("list"); // list | form
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`, { headers });
      setPages(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error("Gabim në ngarkim.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNew = () => {
    setEditing(null);
    setView("form");
  };
  const onEdit = (p) => {
    setEditing(p);
    setView("form");
  };
  const onBack = () => {
    setEditing(null);
    setView("list");
    fetchPages();
  };
  const onSaved = () => {
    fetchPages();
    setView("list");
    setEditing(null);
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Fshini faqen "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${p.id}`, { headers });
      toast.success("U fshi.");
      fetchPages();
    } catch {
      toast.error("Gabim në fshirje.");
    }
  };

  if (loading) {
    return <p className="text-sm-muted py-20 text-center">Duke ngarkuar...</p>;
  }

  if (view === "form") {
    return <PageForm page={editing} token={token} onBack={onBack} onSaved={onSaved} />;
  }
  return <PagesList pages={pages} onNew={onNew} onEdit={onEdit} onDelete={onDelete} />;
};

export default PagesEditor;
